import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';
import FormSelect from 'src/components/form/FormSelect';
import InstitutionIcon from 'src/assets/icons/school-building.svg';
import INSTITUTION_LIST from 'src/graphql/query/institution-list.graphql';
import { listToSelectOptions } from 'src/utils';

import type { Institution } from 'src/types/institution';

export const InstitutionSchema = z.object({
  institutionId: z.string(),
});

const InstitutionField = ({
  name,
  organizationId,
  selectedInstitution,
  className,
  isRequired = false,
  showSearch = true,
  optionKey = 'institutionId',
  label = 'University',
  placeholder = 'Select your university',
}: {
  name: string;
  selectedInstitution?: Institution;
  organizationId?: string;
  className?: string;
  isRequired?: boolean;
  showSearch?: boolean;
  optionKey?: string;
  label?: string;
  placeholder?: string;
}) => {
  const { data, loading, refetch } = useQuery(INSTITUTION_LIST, {
    variables: { organizationId },
  });

  const list = useMemo(() => {
    const dynamicList = (data?.institutionsList ?? []) as Institution[];
    let finalList = dynamicList;

    if (selectedInstitution?.institutionId) {
      finalList = [
        selectedInstitution,
        ...dynamicList.filter(
          (i) =>
            Number(i.institutionId) !==
            Number(selectedInstitution?.institutionId)
        ),
      ];
    }

    return listToSelectOptions(finalList, optionKey);
  }, [data?.institutionsList, optionKey, selectedInstitution]);

  const debouncedSearch = useDebouncedCallback(async (value: string) => {
    if (value.length && value.length < 3) return;

    await refetch({ query: value });
  }, 300);

  return (
    <FormSelect
      name={name}
      isDisabled={loading}
      listLoading={loading}
      IconLeft={InstitutionIcon}
      label={label}
      placeholder={placeholder}
      items={list}
      className={className}
      isRequired={isRequired}
      {...(showSearch && { onSearch: debouncedSearch })}
    />
  );
};

export default InstitutionField;
